import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  ITracker,
  IQueryParam,
  IPopulate,
  ICreateTracker,
  IUpdateTracker,
  TipoTracker,
  Operador,
  IServicioContratado,
  IGetDeviceProfileChirpstack,
  IFilter,
  ISim,
} from 'modelos/src';
import { Observable, Subscription, firstValueFrom, map, startWith } from 'rxjs';
import { TrackersService } from '../trackers.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { ParamsService } from '../../../../../auxiliares/servicios/params.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LoginService } from '../../../../login/login.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import {
  ICheckSimResult,
  SimsService,
} from '../../../../../auxiliares/servicios/http/sims.service';

/** 8 bytes en hexadecimal: devEUI y joinEUI. */
const HEX_16 = /^[0-9a-fA-F]{16}$/;
/** 16 bytes en hexadecimal: appKey (OTAA). */
const HEX_32 = /^[0-9a-fA-F]{32}$/;

@Component({
  selector: 'app-crear-editar-tracker',
  templateUrl: './crear-editar-tracker.component.html',
  styleUrl: './crear-editar-tracker.component.scss',
  standalone: false,
})
export class CrearEditarTrackerComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<CrearEditarTrackerComponent>>(MatDialogRef);
  private lista2 = inject(Lista2Service);
  private service = inject(TrackersService);
  private simsService = inject(SimsService);
  helper = inject(HelperService);
  dialog = inject(MatDialog);
  private dialogService = inject(DialogService);
  paramsService = inject(ParamsService);

  /** Carga inicial del formulario. Mientras está en true el form NO se renderiza. */
  public loading = false;
  /**
   * Guardado en curso. Va aparte de `loading` a propósito: `loading` desmonta el
   * formulario (`@if (!loading)`), así que usarlo durante el submit dejaba la
   * tarjeta vacía y gris de fondo mientras se veía el diálogo de confirmación.
   * `guardando` solo deshabilita el botón y deja el form a la vista.
   */
  public guardando = false;
  public clientePropio = LoginService.getCliente();
  public id: string | null = null;
  public title = '';
  public subtitle?: string;
  public tipos?: TipoTracker[] = ['GPRS', 'Qualcomm', 'T1000-B'];
  public tracker?: ITracker;
  public servicios?: IServicioContratado[] = [];

  public tieneImei = false;

  // ── LoRaWAN (T1000-B) ────────────────────────────────────────────────────
  // El T1000-B ES el dispositivo LoRaWAN: no hay un nodo aparte que elegir, los
  // datos de radio se cargan acá mismo y el backend provisiona el device en
  // ChirpStack al guardar.
  public deviceProfiles: IGetDeviceProfileChirpstack[] = [];
  public mostrarAvanzadosLora = false;
  public trackerConflicto?: ITracker;
  public verificandoDeveui = false;
  public deveuiVerificado = false;
  private deveui$?: Subscription;
  public mostrarSim2 = false;
  public duplicadoSim1?: ICheckSimResult['entidad'];
  public duplicadoSim2?: ICheckSimResult['entidad'];
  public cargandoCheckSim1 = false;
  public cargandoCheckSim2 = false;

  public apns = [
    'igprs.claro.com.ar',
    'wap.grps.unifon.com.ar',
    'datos.personal.com',
  ];

  filteredOptionsSim1 = new Observable<string[]>();
  filteredOptionsSim2 = new Observable<string[]>();

  public operadores: Operador[] = [
    'Claro',
    'Movistar',
    'Personal',
    'Tuenti',
    'Otro',
  ];

  public form?: FormGroup;
  public tipoCliente = HelperService.getTipoCliente();
  private tipo$?: Subscription;

  get qualcomm() {
    return this.form?.get('qualcomm') as FormGroup;
  }

  get t1000b() {
    return this.form?.get('t1000b') as FormGroup;
  }

  /** 'crear' o 'editar', para armar frases sin repetir la palabra "tracker". */
  get verbo(): string {
    return this.id ? 'editar' : 'crear';
  }

  /** En edición el devEUI es la identidad del equipo: no se puede cambiar. */
  get deveuiEditable(): boolean {
    return !this.id;
  }

  get sim1() {
    return this.form?.get('sim1') as FormGroup;
  }

  get sim2() {
    return this.form?.get('sim2') as FormGroup;
  }

  get idCliente() {
    return this.form?.get('idCliente')?.value;
  }

  private async listarServicios(): Promise<void> {
    const query: IQueryParam = {
      sort: 'nombre',
      select: 'nombre icono',
    };
    this.servicios = (
      await firstValueFrom(this.lista2.serviciosContratados(query))
    ).datos;
  }

  private setupConditionalValidators() {
    const uniqueId = this.form.get('uniqueId');
    const deveui = this.t1000b.get('deveui');
    const appkey = this.t1000b.get('appkey');
    const deviceProfileId = this.t1000b.get('deviceProfileId');

    const applyValidators = (tipo: TipoTracker | null) => {
      uniqueId.clearValidators();
      deveui.clearValidators();
      appkey.clearValidators();
      deviceProfileId.clearValidators();
      this.t1000b.get('joineui')?.clearValidators();
      if (tipo === 'GPRS') {
        uniqueId.setValidators([Validators.required]);
      } else if (tipo === 'T1000-B') {
        // El backend provisiona el device en ChirpStack al guardar: sin estos
        // tres no hay alta posible. La appKey es obligatoria porque el T1000-B
        // es OTAA — sin ella el backend tomaría el camino ABP.
        //
        // Los largos son los de LoRaWAN: devEUI y joinEUI son 8 bytes (16
        // caracteres hex), la appKey 16 bytes (32). Se validan acá porque, si
        // no, ChirpStack rechaza el alta con un escueto "Invalid string length"
        // que no dice ni qué campo ni cuánto esperaba.
        deveui.setValidators([Validators.required, Validators.pattern(HEX_16)]);
        appkey.setValidators([Validators.required, Validators.pattern(HEX_32)]);
        deviceProfileId.setValidators([Validators.required]);
        this.t1000b.get('joineui')?.setValidators([Validators.pattern(HEX_16)]);
        this.t1000b
          .get('joineui')
          ?.updateValueAndValidity({ emitEvent: false });
        void this.listarDeviceProfiles();
      }
      for (const control of [uniqueId, deveui, appkey, deviceProfileId]) {
        control.updateValueAndValidity({ emitEvent: false });
      }
    };

    // Apply for current value (edit mode)
    applyValidators(this.form.get('tipo').value);

    // Subscribe to future changes
    this.tipo$ = this.form.get('tipo').valueChanges.subscribe(applyValidators);
  }

  private createForm() {
    this.form = new FormGroup({
      nombre: new FormControl(this.tracker?.nombre, Validators.required),
      identificacion: new FormControl(this.tracker?.identificacion),
      idCliente: new FormControl(
        this.tracker?.idCliente ?? this.clientePropio?._id,
      ),
      uniqueId: new FormControl(this.tracker?.uniqueId),
      tipo: new FormControl(this.tracker?.tipo || 'GPRS', Validators.required),
      frecReporte: new FormControl(this.tracker?.frecReporte),
      qualcomm: new FormGroup({
        serialNumber: new FormControl(this.tracker?.qualcomm?.serialNumber),
      }),
      // LoRaWAN (T1000-B): FormGroup anidado, espejo del sub-documento
      // `t1000b` del modelo.
      t1000b: new FormGroup({
        deveui: new FormControl({
          value: this.tracker?.t1000b?.deveui ?? null,
          disabled: !this.deveuiEditable,
        }),
        appkey: new FormControl(this.tracker?.t1000b?.appkey),
        joineui: new FormControl(this.tracker?.t1000b?.joineui),
        deviceProfileId: new FormControl(this.tracker?.t1000b?.deviceProfileId),
        tiempoLimiteComunicacion: new FormControl(
          this.tracker?.t1000b?.tiempoLimiteComunicacion ?? 24,
        ),
        isDisabled: new FormControl(this.tracker?.t1000b?.isDisabled ?? false),
        skipFcntCheck: new FormControl(
          this.tracker?.t1000b?.skipFcntCheck ?? false,
        ),
      }),
      idModelo: new FormControl(this.tracker?.idModelo),
      sim1: new FormGroup({
        // SIM vinculada o elegida en el selector de disponibles
        _id: new FormControl(this.tracker?.sim1?._id),
        iccid: new FormControl(this.tracker?.sim1?.iccid),
        numero: new FormControl(this.tracker?.sim1?.numero),
        operador: new FormControl(this.tracker?.sim1?.operador),
        apn: new FormControl(this.tracker?.sim1?.apn),
        usuario: new FormControl(this.tracker?.sim1?.usuario),
        password: new FormControl(this.tracker?.sim1?.password),
      }),
      sim2: new FormGroup({
        // SIM vinculada o elegida en el selector de disponibles
        _id: new FormControl(this.tracker?.sim2?._id),
        iccid: new FormControl(this.tracker?.sim2?.iccid),
        numero: new FormControl(this.tracker?.sim2?.numero),
        operador: new FormControl(this.tracker?.sim2?.operador),
        apn: new FormControl(this.tracker?.sim2?.apn),
        usuario: new FormControl(this.tracker?.sim2?.usuario),
        password: new FormControl(this.tracker?.sim2?.password),
      }),
      idServiciosContratados: new FormControl(
        this.tracker?.idServiciosContratados,
      ),
      idsClientesQuePuedenAtenderEventosTecnicos: new FormControl(
        this.tracker?.idsClientesQuePuedenAtenderEventosTecnicos ?? null,
      ),
      configHorariosAtencion: new FormControl(
        this.tracker?.configHorariosAtencion ??
          (this.clientePropio?._id
            ? [{ idCliente: this.clientePropio._id }]
            : null),
      ),
      configHorariosAtencionTecnica: new FormControl(
        this.tracker?.configHorariosAtencionTecnica ??
          (this.clientePropio?._id
            ? [{ idCliente: this.clientePropio._id }]
            : null),
      ),
      puedeSolicitarServicioTecnico: new FormControl(
        this.tracker?.puedeSolicitarServicioTecnico ?? false,
      ),
    });

    this.setupConditionalValidators();
  }

  private getData() {
    // getRawValue y no value: en edición el control `deveui` está deshabilitado
    // y `value` lo omitiría.
    const data: ICreateTracker | IUpdateTracker = this.form?.getRawValue();
    // No ensuciar el documento con el sub-objeto LoRaWAN en trackers que no lo
    // son. El backend igual solo lo mira si tipo === 'T1000-B'.
    if (data.tipo !== 'T1000-B') {
      delete data.t1000b;
    } else if (data.t1000b?.deveui) {
      // El backend lo persiste en mayúscula (setter de `t1000b.deveui`);
      // mandarlo ya normalizado evita que el aviso de duplicado falle por el
      // case.
      data.t1000b.deveui = data.t1000b.deveui.toUpperCase();
    }
    if (this.paramsService.getParams()['idCliente'])
      data.idCliente = this.paramsService.getParams()['idCliente'];
    const idsAtencion = (data.configHorariosAtencion ?? [])
      .map((c: any) => c.idCliente)
      .filter(Boolean) as string[];
    const idsTecnicos = (data.configHorariosAtencionTecnica ?? [])
      .map((c: any) => c.idCliente)
      .filter(Boolean) as string[];
    (data as any).idsClientesQuePuedenAtender = idsAtencion;
    data.idsClientesQuePuedenAtenderEventosTecnicos = idsTecnicos.length
      ? idsTecnicos
      : null;
    return data;
  }

  /** Un slot de SIM se persiste si tiene ALGÚN dato, no solo iccid. */
  private simTieneDatos(sim: any): boolean {
    if (!sim) return false;
    return ['iccid', 'numero', 'operador', 'apn', 'usuario', 'password'].some(
      (k) => !!sim[k],
    );
  }

  /**
   * SIM como entidad aparte: por cada slot con datos, persiste la SIM
   * (POST /sims/upsert) y setea idSim1/idSim2 en el tracker. El `_id` del grupo
   * (la SIM ya vinculada o la elegida en el selector de disponibles) hace que
   * sin iccid se actualice esa SIM en vez de crear otra.
   * Elimina el sub-objeto embebido sim1/sim2 del payload (ya no es del modelo).
   */
  /** Hay una SIM vinculada o elegida en el selector para ese slot. */
  public conSim(slot: 1 | 2): boolean {
    return !!(slot === 1 ? this.sim1 : this.sim2)?.get('_id')?.value;
  }

  /** SIM elegida en el selector de disponibles (o `null` = cargar una nueva). */
  public alSeleccionarSim(slot: 1 | 2, sim: ISim | null): void {
    SimsService.aplicarSeleccion(slot === 1 ? this.sim1 : this.sim2, sim);
    if (slot === 1) this.duplicadoSim1 = undefined;
    else this.duplicadoSim2 = undefined;
  }

  private async aplicarSims(data: ICreateTracker | IUpdateTracker) {
    const raw: any = this.form?.value;
    for (const slot of [1, 2] as const) {
      const simVal = raw?.['sim' + slot];
      const idField = ('idSim' + slot) as 'idSim1' | 'idSim2';
      if (this.simTieneDatos(simVal)) {
        const { _id, ...datos } = simVal;
        const payload = _id ? { ...datos, _id } : datos;
        const sim = await this.simsService.upsert(payload);
        data[idField] = sim._id;
      } else {
        data[idField] = null;
      }
    }
    delete (data as any).sim1;
    delete (data as any).sim2;
  }

  /**
   * La SIM es una entidad aparte (FK idSim1/idSim2 + virtuals sim1/sim2), así
   * que solo llega si quien abre el dialog pidió populate. Red de seguridad
   * para los orígenes que pasen un tracker sin populate: si hay FK pero no
   * vienen las SIMs, se rehidrata acá — el único punto por el que pasan todos
   * los orígenes del dialog.
   */
  private async hidratarSims(): Promise<void> {
    if (!this.tracker?._id) return;
    const faltaSim1 = !!this.tracker.idSim1 && !this.tracker.sim1;
    const faltaSim2 = !!this.tracker.idSim2 && !this.tracker.sim2;
    if (!faltaSim1 && !faltaSim2) return;
    const populate: IPopulate[] = [{ path: 'sim1' }, { path: 'sim2' }];
    const query: IQueryParam = {
      filter: JSON.stringify({ _id: this.tracker._id }),
      populate: JSON.stringify(populate),
      limit: 1,
    };
    try {
      const listado = await this.service.getFiltered(query);
      const conSims = listado?.datos?.[0];
      if (!conSims) return;
      this.tracker = {
        ...this.tracker,
        idSim1: conSims.idSim1,
        idSim2: conSims.idSim2,
        sim1: conSims.sim1,
        sim2: conSims.sim2,
      };
    } catch (error) {
      console.error(error);
    }
  }

  // Actions

  public async onSubmit() {
    // Si algo quedó inválido, marcar todo como touched: sin esto Material no
    // pinta los mat-error (solo los muestra en errorState = inválido + touched)
    // y el usuario ve el botón sin efecto y ningún motivo.
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.helper.notifError(
        'Revisá los campos marcados en rojo.',
        'Cerrar',
        true,
      );
      return;
    }

    // Alta de un T1000-B: igual que en dispositivos LoRaWAN, se ofrece crear
    // también el device en ChirpStack. Destildarlo sirve para dar de alta en
    // IRIX un equipo que ya existe en el Network Server.
    const esAltaLorawan = !this.id && this.form.value.tipo === 'T1000-B';
    let crearEnServer = true;
    let res: boolean;

    if (esAltaLorawan) {
      const conf = await this.dialogService.confirm(
        this.title,
        `¿Querés ${this.verbo} el tracker?`,
        'Aceptar',
        'Cancelar',
        true,
        'Crear también en LoRaWAN Server',
        true,
      );
      res = conf.confirmed;
      crearEnServer = conf.checkbox;
    } else {
      res = await this.dialogService.confirm(
        this.title,
        `¿Querés ${this.verbo} el tracker?`,
      );
    }

    if (!res) {
      return;
    }
    if (this.id && this.form.value.uniqueId !== this.tracker.uniqueId) {
      res = await this.dialogService.confirm(
        this.title,
        `¿Cambiar el valor uniqueId borrara los registros anteriores, continuar?`,
      );
      if (!res) {
        return;
      }
    }

    // Recién ahora: con los confirms aceptados. Antes de este punto el form
    // tiene que seguir visible detrás del diálogo.
    this.guardando = true;

    try {
      if (this.id) {
        // Update
        const update = this.getData() as IUpdateTracker;
        if (!update) return;
        await this.aplicarSims(update);
        const result = await this.service.update(this.id, update);
        this.helper.notifSuccess('Tracker Editado');
        this.dialogRef.close(result);
      } else {
        // Create
        const create = this.getData() as ICreateTracker;
        if (!create) return;
        await this.aplicarSims(create);
        const result = await this.service.create(create, crearEnServer);
        this.helper.notifSuccess('Tracker Creado');
        this.dialogRef.close(result);
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.guardando = false;
  }

  private async listarDeviceProfiles(): Promise<void> {
    if (this.deviceProfiles.length) return;
    try {
      this.deviceProfiles = await firstValueFrom(this.lista2.deviceProfiles());
    } catch (error) {
      console.error('Error listando device profiles', error);
      this.deviceProfiles = [];
    }
  }

  /** appKey / joinEUI aleatorios, mismo helper que el form de dispositivo LoRaWAN. */
  public generarRandomHexString(length: number): string {
    const characters = '0123456789ABCDEF';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(
        Math.floor(Math.random() * characters.length),
      );
    }
    return result;
  }

  public generarAppkey(): void {
    this.t1000b?.get('appkey')?.setValue(this.generarRandomHexString(32));
  }

  /**
   * Avisa si el devEUI ya está tomado por otro tracker. No bloquea el submit:
   * el backend es el que decide (y ChirpStack rechaza el duplicado igual).
   */
  public async verificarDeveuiExistente(deveui: string): Promise<void> {
    const valor = deveui?.trim().toUpperCase();
    if (!valor) {
      this.trackerConflicto = undefined;
      this.deveuiVerificado = false;
      return;
    }
    this.verificandoDeveui = true;
    this.deveuiVerificado = false;
    try {
      const filter: IFilter<ITracker> = {
        't1000b.deveui': valor,
      } as unknown as IFilter<ITracker>;
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        select: '_id nombre identificacion',
        limit: 1,
      };
      const res = await this.service.getFiltered(query);
      const encontrado = res?.datos?.[0];
      this.trackerConflicto =
        encontrado && encontrado._id !== this.id ? encontrado : undefined;
      this.deveuiVerificado = true;
    } catch (error) {
      console.error('Error verificando el devEUI', error);
    } finally {
      this.verificandoDeveui = false;
    }
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();

    return this.apns.filter((option) =>
      option.toLowerCase().includes(filterValue),
    );
  }

  // Hooks
  async ngOnInit() {
    this.loading = true;
    const params: ITracker = this.paramsService.getParams()['tracker'];
    if (params) {
      this.tracker = params;
      this.id = params._id;
    }

    if (this.id) {
      this.title = 'Editar Tracker';
      this.subtitle = this.tracker.nombre;
      // Traer sim1/sim2 si el origen del dialog no las populó
      await this.hidratarSims();
      // In edit mode, detect if uniqueId looks like an IMEI
      if (this.tracker?.uniqueId?.length >= 14) {
        this.tieneImei = true;
      }
      // In edit mode, show SIM 2 if it has data
      const s2 = this.tracker?.sim2;
      if (s2?.iccid || s2?.numero || s2?.operador || s2?.apn) {
        this.mostrarSim2 = true;
      }
    } else {
      this.title = 'Crear Tracker';
    }

    this.createForm();

    // Preseleccionar el cliente del permiso activo al crear
    if (!this.tracker) {
      this.form.patchValue({
        idCliente: this.clientePropio?._id,
      });
    }

    this.filteredOptionsSim1 = this.sim1.get('apn').valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value || '')),
    );

    this.filteredOptionsSim2 = this.sim2.get('apn').valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value || '')),
    );

    await Promise.all([this.listarServicios()]);

    this.loading = false;
  }

  public async checkSimDuplicado(simNumero: 1 | 2): Promise<void> {
    const formSim = simNumero === 1 ? this.sim1 : this.sim2;
    const iccid = formSim.get('iccid')?.value || undefined;
    const numero = formSim.get('numero')?.value || undefined;

    if (!iccid && !numero) {
      if (simNumero === 1) this.duplicadoSim1 = undefined;
      else this.duplicadoSim2 = undefined;
      return;
    }

    if (simNumero === 1) this.cargandoCheckSim1 = true;
    else this.cargandoCheckSim2 = true;

    try {
      const result = await this.simsService.check(
        iccid,
        numero,
        this.id || undefined,
      );
      if (simNumero === 1) this.duplicadoSim1 = result.entidad;
      else this.duplicadoSim2 = result.entidad;
    } catch {
      // Ignorar errores de chequeo
    } finally {
      if (simNumero === 1) this.cargandoCheckSim1 = false;
      else this.cargandoCheckSim2 = false;
    }
  }

  public tipoLabelSim(simNumero: 1 | 2): string {
    const duplicado = simNumero === 1 ? this.duplicadoSim1 : this.duplicadoSim2;
    if (!duplicado) return '';
    return duplicado.tipo === 'tracker' ? 'tracker' : 'dispositivo de alarma';
  }

  public async moverSim(simNumero: 1 | 2): Promise<void> {
    const duplicado = simNumero === 1 ? this.duplicadoSim1 : this.duplicadoSim2;
    if (!duplicado) return;

    const ok = await this.dialogService.confirm(
      'Mover SIM',
      `¿Querés quitar la SIM del ${this.tipoLabelSim(simNumero)} "${duplicado.nombre}" (SIM ${duplicado.simNumero}) para asignarla aquí?`,
    );
    if (!ok) return;

    try {
      await this.simsService.liberar({
        idEntidad: duplicado._id,
        tipo: duplicado.tipo,
        simNumero: duplicado.simNumero,
      });
      if (simNumero === 1) this.duplicadoSim1 = undefined;
      else this.duplicadoSim2 = undefined;
      this.helper.notifSuccess('SIM liberada del dispositivo anterior');
    } catch (error) {
      this.helper.notifError(error);
    }
  }

  ngOnDestroy(): void {
    this.tipo$?.unsubscribe();
    this.deveui$?.unsubscribe();
  }
}
